import { Injectable, PipeTransform, QueryList, ViewChildren } from '@angular/core';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';

import { DecimalPipe } from '@angular/common';
import { debounceTime, delay, switchMap, tap } from 'rxjs/operators';
import { NgbdSortableHeader, SortDirection, SortEvent } from './../directives/sortable.directive';
import * as XLSX from 'xlsx';
import { Clipboard } from '@angular/cdk/clipboard';
import { jsPDF } from "jspdf";
import { ToastrService } from 'ngx-toastr';
import 'jspdf-autotable';

interface SearchResult {
  dataSoruces: any[];
  total: number;
}

interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  sortColumn: string;
  sortDirection: SortDirection;
}
interface CellData {
  cell: {
    styles: {
      fillColor: string | undefined | number[];
      fontStyle: string;
      textColor: [number, number, number];
    };
  };
  row: {
    index: number;
  };
}


function compare(v1, v2) {
  return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
}

function sort(dataSoruces: any[], column: string, direction: string): any[] {
  if (direction === '') {
    return dataSoruces;
  } else {
    return [...dataSoruces].sort((a, b) => {
      const res = compare(a[column], b[column]);
      return direction === 'asc' ? res : -res;
    });
  }
}

// function matches(dataSoruce: any, term: string, pipe: PipeTransform) {
//   // return dataSoruce.Title.toLowerCase().includes(term);
//   // || pipe.transform(dataSoruce.area).includes(term)
//   // || pipe.transform(dataSoruce.population).includes(term);

//   return eval(Object.getOwnPropertyNames(dataSoruce).filter(e => dataSoruce[e] != null).map(e => typeof (dataSoruce[e]) === 'string' ?
//     `dataSoruce['${e}'].toLowerCase().includes(term)` :
//     (typeof (dataSoruce[e]) === 'number' ? `pipe.transform(dataSoruce['${e}']).includes(term)` : `dataSoruce['${e}'].toString().includes(term)`)).join(' || '));

// }


function matches(dataSource: any, term: string, pipe: PipeTransform) {
  for (const key of Object.keys(dataSource)) {
    const value = dataSource[key];
    if (value != null) {
      let formattedProperty = value;
      if (typeof value === 'number') {
        formattedProperty = value.toString();
      } else if (typeof value === 'string') {
        formattedProperty = value.toLowerCase();
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          formattedProperty = numValue.toString();
        }
      }
      if (formattedProperty.toString().includes(term.toLowerCase())) {
        return true;
      }
    }
  }
  return false;
}

@Injectable()
export class DttableService {
  dataSoruces = [];
  private _loading$ = new BehaviorSubject<boolean>(true);
  private _search$ = new Subject<void>();
  private _dataSoruces$ = new BehaviorSubject<any[]>([]);
  private _total$ = new BehaviorSubject<number>(0);
  private _state: State = {
    page: 1,
    pageSize: 10,
    searchTerm: '',
    sortColumn: '',
    sortDirection: ''
  };

  // @ViewChildren(NgbdSortableHeader) headers: QueryList<NgbdSortableHeader>;

  constructor(private pipe: DecimalPipe,
    private clipboard: Clipboard,
    private toaster: ToastrService
  ) {
    this._search$.pipe(
      tap(() => this._loading$.next(true)),
      debounceTime(200),
      switchMap(() => this._search()),
      delay(200),
      tap(() => this._loading$.next(false))
    ).subscribe(result => {
      this._dataSoruces$.next(result.dataSoruces);
      this._total$.next(result.total);
    });

    this._search$.next();
  }
  pdfSize : string = '';
  get dataSoruces$() { return this._dataSoruces$.asObservable(); }
  get total$() { return this._total$.asObservable(); }
  get loading$() { return this._loading$.asObservable(); }
  get page() { return this._state.page; }
  get pageSize() { return this._state.pageSize; }
  get searchTerm() { return this._state.searchTerm; }

  set page(page: number) { this._set({ page }); }
  set pageSize(pageSize: number) { this._set({ pageSize }); }
  set searchTerm(searchTerm: string) { this._set({ searchTerm }); }
  set sortColumn(sortColumn: string) { this._set({ sortColumn }); }
  set sortDirection(sortDirection: SortDirection) { this._set({ sortDirection }); }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  private _search(): Observable<SearchResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._state;

    // 1. sort
    let dataSoruces = sort(this.dataSoruces, sortColumn, sortDirection);

    // 2. filter
    dataSoruces = dataSoruces.filter(dataSoruce => matches(dataSoruce, searchTerm, this.pipe));
    const total = dataSoruces.length;

    // 3. paginate
    dataSoruces = dataSoruces.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
    return of({ dataSoruces, total });
  }

  getDataSource(dataSource) {
    this.dataSoruces = dataSource;
    this._search$.pipe(
      tap(() => this._loading$.next(true)),
      debounceTime(200),
      switchMap(() => this._search()),
      delay(200),
      tap(() => this._loading$.next(false))
    ).subscribe(result => {
      this._dataSoruces$.next(result.dataSoruces);
      this._total$.next(result.total);
    });

    this._search$.next();
  }

  setPdfSize(val :string){
    this.pdfSize = val;
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
  }

  onSort({ column, direction }: SortEvent, header) {
    // resetting other headers
    header.forEach(header => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    this.sortColumn = column;
    this.sortDirection = direction;
  }

  export(type, list, header, tableid = '', filename = document.getElementsByTagName('title')[0].text) {
  
    let div = document.getElementById('dttableexportdiv');
    if (div == undefined || div == null) {
      div = document.createElement('div') as HTMLDivElement;
    }
    let cols = [];
    if (tableid != '') {
      const a = Array.from(document.getElementById(tableid).children[0].children[0].children);
      const b = a.filter(e => Array.from(e.attributes).filter(a => a.name === 'data-item').length > 0).map(b => Array.from(b.attributes).filter(e => e.name === 'data-item').map(e => e.value));
      b.map(e => cols = cols.concat(e));
      const title = a.filter(e => Array.from(e.attributes).filter(a => a.name === 'data-item').length > 0).map(b => b.innerHTML);
      let table = `<table id="selectedele" class="selectedele"><tr><td  colspan="${cols.length}" style="text-align: center;"><b>${filename}</b></td></tr><thead><tr>${cols.map((e, i) => '<th><b>' + title[i] + '</b></th>').join('')}</tr></thead><tbody>${list.map(e => '<tr>' + cols.map(a => '<td>' + (e[a] == null ? '' : e[a]) + '</td>').join('') + '</tr>').join('')}</tbody></table>`

      div.id = 'dttableexportdiv';

      div.innerHTML = table;
      div.style.zIndex = '-1';
    }
    if (document.getElementById('dttableexportdiv') == undefined || document.getElementById('dttableexportdiv') == null) {
      document.body.appendChild(div);
    }

    switch (type) {
      case 'excel':
        this.exportToExcel(list, header, 'xlsx', filename);
        break;
      case 'csv':
        this.exportToExcel(list, header, 'csv', filename);
        break;
      case 'pdf':
        this.exportToPDF(list, header, filename);
        break;
      case 'copy':
      
        const headerLine = header.join('\t');
        const dataLines = list.map(row => Object.keys(row).map(key => row[key]).join('\t')).join('\n');
        const tableText = `${headerLine}\n${dataLines}`;
        this.copyToClipboard(tableText);
        const rowCount = list.length; const message = `Copied ${rowCount} row${rowCount > 1 ? 's' : ''} to clipboard`;
        this.toaster.success('Copy to clipboard' + '\n' + message)
        break;
      case 'print':
        const keys = header;
        const printWindow = window.open('', '_blank');
          if (printWindow) {
            const tableColumns = Object.keys(list[0] as unknown as object);
            printWindow.document.open();
            printWindow.document.write(`
            <html>
            <head>
              <title>${filename}</title>
              <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.5.0/dist/css/bootstrap.min.css">
            </head>
            <body>
            <div class="container">
              <div class="table-responsive">
                <table class="table table-striped table-bordered table-hover">
                  <thead class="bg-secondary">
                    <tr>
                      ${keys.map(column => `<th>${column}</th>`).join('')}
                    </tr>
                  </thead>
                  <tbody>
                    ${list.map(row => `
                      <tr>
                        ${tableColumns.map(column => `<td>${(row as any)[column]}</td>`).join('')}
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
            </body>
            </html>`)
            printWindow.document.close();
            printWindow.print();
          }
        break;
    }
    document.getElementById('dttableexportdiv').remove();
  }
  exportToExcel(list, header, type: string, filename: string) {
    let ws: XLSX.WorkSheet | undefined;

    let jsonData
    if (list instanceof HTMLElement) {
      ws = XLSX.utils.table_to_sheet(list);
    }
    if (list instanceof Array) {
      jsonData = XLSX.utils.json_to_sheet(list);

      if (jsonData['!ref']) {
        const range = XLSX.utils.decode_range(jsonData['!ref']);
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: C });
          if (jsonData[cellAddress] && header[C]) {
            jsonData[cellAddress].v = header[C];
          }
        }
      }
    }
    ws = jsonData;

    if (ws) {
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${filename}.${type}`);
    }
  }
  exportToPDF(list, tableHead, filename) {
    let property = localStorage.getItem('Btn-Color-Changer');
    let rgbArray: number[] | undefined | string;
    if (property.toString().includes('#')) {
      rgbArray = property.toString()
    }
    else if (property.toString().includes('rgb')) {
      let rgbValues = property.match(/\d+/g);
      rgbArray = rgbValues?.map(value => parseInt(value, 10));
    }
    else
      rgbArray = property
    if(this.pdfSize != ''){
      this.pdfSize = 'a3';
    }
    else{
      this.pdfSize = 'a4';
    }
    const doc = new jsPDF({ orientation: 'l', unit: 'mm', format: this.pdfSize });
    const dataWithKeys = [tableHead, ...list.map(item => (Object as any).values(item))];
    (doc as any).autoTable({
      body: dataWithKeys, didParseCell: (data: CellData) => {
        if (data.row.index === 0) {
          data.cell.styles.fillColor = rgbArray;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [255, 255, 255];
        }
      }
    });
    doc.save(`${filename}.pdf`);
}



}
